import { useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';

/**
 * Appui long, comme dans les messageries.
 *
 * Le clic droit fait le meme travail sur un ordinateur, ou personne ne
 * maintient le bouton une demi-seconde. Un doigt qui glisse annule : sur
 * telephone, l'appui long et le defilement partent du meme geste, et rien
 * n'est plus penible qu'un menu qui surgit pendant qu'on fait defiler.
 */
const HOLD_MS = 450;
const SLIP_PX = 12;

/**
 * L'element tenu part avec l'appui : la fenetre qui s'ouvre en sort, et
 * il n'y a pas de bouton a qui l'accrocher — c'est la ligne elle-meme
 * qu'on a touchee.
 */
export type PriseLongue = Omit<ReturnType<typeof useLongPress>, 'annuler'>;

/** @see PriseLongue */
export function useLongPress(onLongPress: (trigger: HTMLElement) => void) {
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const tenu = useRef<HTMLElement | null>(null);
  /*
    Le clic qui suit un appui long, a avaler.

    Une bulle de texte ne mene nulle part, et cela n'a longtemps rien
    coute. Une carte de post ou d'evenement, elle, est un lien : le doigt
    releve envoyait un `click`, et l'ecran de l'evenement s'ouvrait
    par-dessus le menu qu'on venait de faire apparaitre.

    Remis a plat a chaque nouvelle prise : si le clic attendu n'arrive
    jamais, le drapeau ne doit pas manger celui d'apres.
  */
  const clicAAvaler = useRef(false);

  function cancel() {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    origin.current = null;
  }

  return {
    /*
      De quoi l'interrompre du dehors.

      Il s'annulait seul passe douze pixels — mais le glissement pour
      repondre capture le pointeur des dix, et les evenements cessent
      alors d'atteindre la bulle : ce seuil n'etait plus jamais franchi.
      L'appui long allait donc au bout pendant qu'on tirait le message,
      et son menu s'ouvrait par-dessus, avec sa copie immobile.
    */
    annuler: cancel,
    onPointerDown(event: ReactPointerEvent) {
      // Un clic droit a deja son propre chemin, plus bas.
      if (event.button !== 0) return;
      clicAAvaler.current = false;
      origin.current = { x: event.clientX, y: event.clientY };
      tenu.current = event.currentTarget as HTMLElement;
      timer.current = window.setTimeout(() => {
        const cible = tenu.current;
        cancel();
        if (!cible) return;
        clicAAvaler.current = true;
        onLongPress(cible);
      }, HOLD_MS);
    },
    onPointerMove(event: ReactPointerEvent) {
      const start = origin.current;
      if (!start) return;
      if (Math.abs(event.clientX - start.x) > SLIP_PX || Math.abs(event.clientY - start.y) > SLIP_PX) {
        cancel();
      }
    },
    onClickCapture(event: ReactMouseEvent) {
      if (!clicAAvaler.current) return;
      clicAAvaler.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onContextMenu(event: ReactMouseEvent) {
      event.preventDefault();
      cancel();
      onLongPress(event.currentTarget as HTMLElement);
    },
  };
}
